<template>
  <div
    ref="DurationDatePicker"
    v-click-outside="$_onClose"
    class="duration-date-picker-wrap"
    :class="{disable}"
  >
    <WsText001
      v-if="titleContent"
      h6
      bold
      class="mb-8"
    >{{ $t(titleContent) }}</WsText001>
    <div
      class="duration-date-picker_date-button"
      :class="{active: isActive.dropdown}"
      @click="$_onToggle()"
    >
      <div class="duration-date-picker_date">
        <i class="icon-date" />
        <p v-if="date.start && date.end">
          {{ $_formatToDot(date.start) }} ~ {{ $_formatToDot(date.end) }}
        </p>
        <p v-else>
          {{ $t(placeholder) }}
        </p>
      </div>
      <i
        v-if="arrowIcon"
        class="icon-chevron-up trailing-icon"
        :class="{show:isActive.dropdown}"
      />
    </div>
    <transition mode="out-in">
      <div
        class="duration-date-picker_custom-datepicker"
        :class="{active: isActive.dropdown, 'drop-up': isDropUp}"
      >
        <div class="date-input">
          <p>{{ $t(title) }}</p>
          <div class="input-container">
            <input
              type="text"
              :value="$_formatToDot(date.start)"
              :placeholder="`${$t('YYYY-MM-DD')}`"
              @change="$_onTypeDate($event, 'start')"
            >
            {{'~'}}
            <input
              type="text"
              :value="$_formatToDot(date.end)"
              :placeholder="`${$t('YYYY-MM-DD')}`"
              @change="$_onTypeDate($event, 'end')"
            >
          </div>
        </div>
        <div class="date-picker-wrap">
          <vc-date-picker
            v-model="date"
            isInline
            tintColor
            mode="range"
            :locale="_locale"
            :attributes="datePickerAttributes"
            class="date-picker"
          />
        </div>
        <div class="action-buttons">
          <WsTextButton001 @click="$_onClear()">
            {{ leftBtnText ? $t(leftBtnText) : $t('清除') }}
          </WsTextButton001>
          <div class="submit-btns">
            <WsTextButton001
              primary
              :isDisable="_disableSubmit"
              @click="$_onSubmit()"
            >
              {{ $t('套用') }}
            </WsTextButton001>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
import ClickOutside from 'vue-click-outside';
import H_time from '@/helpers/time';
import H_common from '@/helpers/common';

export default {
  directives: {
    ClickOutside,
  },
  props: {
    disable: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Object,
      default() {
        return {};
      },
    },
    title: {
      type: String,
      default() {
        return this.$t('自訂');
      },
    },
    placeholder: {
      type: String,
      default() {
        return `${this.$t('YYYY-MM-DD')} ~ ${this.$t('YYYY-MM-DD')}`;
      },
    },
    arrowIcon: {
      type: Boolean,
      default: false,
    },
    titleContent: {
      type: String,
    },
    defaultValue: {
      type: Object,
    },
    scroll: {
      type: Number,
    },
    leftBtnText: {
      type: String,
    }
  },
  data() {
    return {
      isActive: {
        dropdown: false,
      },
      date: {
        start: '',
        end: '',
      },
      isStart: false,
      datePickerAttributes: [
        {
          highlight: {
            class: 'calendar-background',
            contentClass: 'calendar-color',
          },
          dates: this.$moment().toDate(),
        },
      ],
      isDropUp: false,
      dropdownHeight: 0,
      dropdownTop: 0,
    };
  },
  computed: {
    _locale() {
      const localeMap = {
        'tw': 'zh-TW',
        'cn': 'zh-CN',
        'ja': 'ja',
        'us': 'en'
      };
      const locale = this.$store.state.stone_locale.selectingLocale.code;
      return localeMap[locale] || 'zh-TW';
    },
    _disableSubmit() {
      const {start, end} = this.date;
      return this.$_formatToDot(start) === 'Invalid Date' || this.$_formatToDot(end) === 'Invalid Date';
    }
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler() {
        if(this.isStart) return;
        this.$_init();
      },
    },
    scroll() {
      this.$_isDropUp();
    },
  },
  mounted() {
    this.$_isDropUp();
    this.$_init();
    this.isStart = true;
  },
  created() {
    window.addEventListener('resize', this.$_isDropUp);
  },
  beforeDestroy() {
    this.datePickerAttributes = [];
    this.date = { start: '', end: '' };
    window.removeEventListener('resize', this.$_isDropUp);
  },
  methods: {
    $_init() {
      if (!this.value || !this.value.start || !this.value.end) return;
      this.date.start = this.$_formatToDate(this.value.start);
      this.date.end = this.$_formatToDate(this.value.end);
      this.isStart = true;
    },
    $_onToggle() {
      if (this.disable) return;
      this.isActive.dropdown = !this.isActive.dropdown;
    },
    $_onClose() {
      this.isActive.dropdown = false;
    },
    $_onSubmit() {
      if (this._disableSubmit) return;
      const start = H_time.getDateDashFormat(H_time.setTimeToUtc(this.date.start), 'YYYY-MM-DD HH:mm:ssZ');
      const endDate = H_time.endOf(this.date.end, 'YYYY-MM-DD HH:mm:ss', 'day');
      const end = H_time.getDateDashFormat(H_time.setTimeToUtc(endDate), 'YYYY-MM-DD HH:mm:ssZ');
      this.$emit('input', {start, end});
      this.$emit('click');
      this.$_onClose();
    },
    $_formatToDot(date) {
      return H_time.getDateDotFormat(date);
    },
    $_formatToDate(date) {
      if (!date) return '';
      return this.$moment(date).toDate();
    },
    $_onTypeDate(e, type) {
      const isValid = this.$moment(e.target.value).isValid();
      if (!isValid) {
        this.date[type] = null;
        return;
      }
      this.date[type] = this.$_formatToDate(e.target.value);
    },
    $_onClear() {
      this.date = {
        start: this.defaultValue && this.defaultValue.start ? this.$_formatToDate(this.defaultValue.start) : '',
        end: this.defaultValue && this.defaultValue.end ? this.$_formatToDate(this.defaultValue.end) : '',
      };
      this.$_onSubmit();
    },
    $_getDropdownHeight() {
      const {height, y} = H_common.getComponentHeightAndY(this.$refs.DurationDatePicker);
      this.dropdownHeight = height;
      this.dropdownTop = y;
    },
    $_isDropUp() {
      this.$_getDropdownHeight();
      this.isDropUp = H_common.isDropUp(this.dropdownHeight, this.dropdownTop, 400, 400);
    },
  },
};
</script>
<style lang="scss">
@import "@/assets/scss/component/duration-date-picker";
</style>